// src/contexts/SyncStatusContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Global sync status context.
//
//  Exposes:
//    status        — 'synced' | 'pending' | 'offline' | 'error'
//    pendingCount  — number of writes queued but not yet flushed to Firestore
//    lastSyncedAt  — Date | null — timestamp of last successful flush
//
//  HOW IT WORKS:
//    • Polls the IDB pendingQueue every SYNC_STATUS_POLL_INTERVAL_MS to count
//      unsynced records. Interval is configured in config/app.constants.js.
//    • Listens to navigator.online/offline events for the offline status.
//    • SyncStatusContext does NOT write anything — it is read-only.
//    • Polling stops when the component unmounts (sign-out / tab close).
//
//  USAGE:
//    const { status, pendingCount, lastSyncedAt } = useSyncStatus();
//
//  PLACEMENT:
//    <SyncStatusProvider> must be inside <AuthProvider> and <BrowserRouter>
//    so it can read auth state if needed in future, but it has no hard
//    dependency on auth for the current implementation.
//
//  FIRESTORE QUOTA IMPACT: zero — this context only reads IDB locally.
// ─────────────────────────────────────────────────────────────────────────────
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { getPendingQueue }              from '@/lib/db/storageAdapter';
import { isOnline, onOnlineChange }     from '@/lib/sync/onlineStatus';
import { SYNC_STATUS_POLL_INTERVAL_MS } from '@/config/app.constants';
import { useFirestoreQuota } from '@/hooks/sync/useFirestoreQuota';

// ── Context ───────────────────────────────────────────────────────────────────
const SyncStatusContext = createContext(null);

export function SyncStatusProvider({ children }) {
  const [online,       setOnline]       = useState(() => isOnline());
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const intervalRef = useRef(null);

  // ── Count pending queue ───────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const queue = await getPendingQueue();
      const count = queue.length;
      setPendingCount(count);

      // When queue drains to zero AND we are online, record a successful sync.
      if (count === 0 && isOnline()) {
        setLastSyncedAt(new Date());
      }
    } catch {
      // IDB not available (e.g. private mode without storage) — treat as 0
      setPendingCount(0);
    }
  }, []);

  // ── Online/offline listener ───────────────────────────────────────────────
  useEffect(() => {
    const unsub = onOnlineChange((nowOnline) => {
      setOnline(nowOnline);
      // Refresh queue count immediately on reconnect — sync may have flushed
      if (nowOnline) refresh();
    });
    return unsub;
  }, [refresh]);

  // ── Polling ───────────────────────────────────────────────────────────────
  useEffect(() => {
    refresh(); // immediate first read
    intervalRef.current = setInterval(refresh, SYNC_STATUS_POLL_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [refresh]);

  // ── Derive status string ──────────────────────────────────────────────────
  // Priority: offline > pending > synced
  let status;
  if (!online)               status = 'offline';
  else if (pendingCount > 0) status = 'pending';
  else                       status = 'synced';

  // ── Firestore quota monitoring ───────────────────────────────────────────────
  const quota = useFirestoreQuota();

  const value = { 
    status, 
    pendingCount, 
    lastSyncedAt, 
    online,
    // Quota info exposed to consumers
    quotaWriteCount: quota.writeCount,
    quotaReadCount: quota.readCount,
    quotaWritePct: quota.writePercentage,
    quotaReadPct: quota.readPercentage,
    quotaWarning: quota.showWarning,
    quotaWarningMessage: quota.warningMessage,
  };

  return (
    <SyncStatusContext.Provider value={value}>
      {children}
    </SyncStatusContext.Provider>
  );
}

export function useSyncStatus() {
  const ctx = useContext(SyncStatusContext);
  if (!ctx) throw new Error('useSyncStatus must be used inside <SyncStatusProvider>');
  return ctx;
}